import React, { useEffect, useId } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createSlug } from "../utils/slug";

const BannerCollectionSlider = ({
  image,
  images,
  bannerOffers, // New prop: array of banner offer objects from API
  title,
  subtitle,
  buttonText,
  buttonLink,
}) => {
  const uniqueId = useId().replace(/:/g, "-");
  const sliderClass = `tf-banner-collection-slider-${uniqueId}`;
  const navigate = useNavigate();

  // Use banner offers if provided, otherwise fall back to images prop
  const itemsToRender =
    bannerOffers && bannerOffers.length > 0
      ? bannerOffers.map((offer) => ({
          image: offer.image_url || offer.image_url_key,
          offerId: offer.id,
          name: offer.name,
          slug: offer.name ? createSlug(offer.name) : null,
        }))
      : (images || (image ? [image] : [])).map((img) => ({
          image: img,
          offerId: null,
          name: null,
          slug: null,
        }));

  useEffect(() => {
    // Wait for DOM to be ready and Swiper to be available
    const initializeSlider = () => {
      const sliderElement = document.querySelector(`.${sliderClass}`);
      if (!window.Swiper || !sliderElement) {
        setTimeout(initializeSlider, 100);
        return;
      }

      // Destroy previous instance if it exists
      if (sliderElement.swiper) {
        try {
          sliderElement.swiper.destroy(true, true);
        } catch (e) {
          // no-op
        }
      }

      new window.Swiper(`.${sliderClass}`, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: itemsToRender.length > 1,
        autoplay: {
          delay: 2000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        },
        speed: 1000,
        pagination: {
          el: `.sw-pagination-banner-${uniqueId}`,
          clickable: true,
        },
        observer: true,
        observeParents: true,
      });
    };

    initializeSlider();

    // Cleanup on unmount
    return () => {
      const sliderElement = document.querySelector(`.${sliderClass}`);
      if (sliderElement && sliderElement.swiper) {
        try {
          sliderElement.swiper.destroy(true, true);
        } catch (e) {
          // no-op
        }
      }
    };
  }, [sliderClass, uniqueId, itemsToRender.length]);

  const handleSlideClick = (item) => {
    if (item.slug && item.offerId) {
      // Include both slug (for SEO) and offer_id (for immediate API call)
      navigate(`/shop/default?offer=${encodeURIComponent(item.slug)}&offer_id=${item.offerId}`);
    } else if (item.slug) {
      navigate(`/shop/default?offer=${encodeURIComponent(item.slug)}`);
    } else if (item.offerId) {
      // Fallback to offer_id if slug is not available
      navigate(`/shop/default?offer_id=${item.offerId}`);
    }
  };

  return (
    <section className="flat-spacing-10">
      <div className="container">
        <div
          className={`swiper ${sliderClass} tf-banner-collection text-center`}
        >
          <div className="swiper-wrapper">
            {itemsToRender.map((item, index) => (
              <div
                key={item.offerId || index}
                className="swiper-slide"
                style={item.offerId ? { cursor: "pointer" } : {}}
                onClick={() => (item.offerId || item.slug) && handleSlideClick(item)}
              >
                <img
                  className="lazyload radius-10"
                  data-src={item.image}
                  src={item.image}
                  alt={item.name || `banner-${index + 1}`}
                  loading="lazy"
                />
                <div className="box-content w-100">
                  <div className="container wow fadeInUp" data-wow-delay="0s">
                    {title && (
                      <h2 className="heading fw-6 text_white">{title}</h2>
                    )}
                    {subtitle && <p className="text_white">{subtitle}</p>}
                    {buttonText && (
                      <Link
                        to={buttonLink || "/shop/collection"}
                        className="tf-btn btn-line btn-line-light collection-other-link fw-6"
                      >
                        <span>{buttonText}</span>
                        <i className="icon icon-arrow1-top-left"></i>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="wrap-pagination">
            <div className="container">
              <div
                className={`sw-dots sw-pagination-banner-${uniqueId} justify-content-center`}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BannerCollectionSlider;
